import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  mistOrganizationIdSelector,
  mistSiteIdSelector,
  siteWlansFetchDataSelector,
  siteClientsFetchDataSelector,
  orgSiteWlansFetchDataSelector,
} from 'features/networkAi/selectors'
import {
  fetchSiteWlansThunk,
  fetchSiteClientsThunk,
  createOrGetNetworkHomeThunk,
  fetchOrgSiteWlansThunk,
} from 'features/networkAi/slice'

export interface useCurrentHomeSiteIdParams {
  homeId: string
}

export interface useCurrentSiteIdParams {
  siteId: string
}

export interface NetworkWLANIdParams {
  wlanId: string
}

export interface NetworkHomeJobsParams {
  rootDomainId: string
  homeId: string
  jobId: string
}

export interface NetworkHomeSubParams {
  renewalDate: string
  totalCost: string
  subActive: string
}

export interface NetworkWLANsCounts {
  wlansTotal: number
  wlansEnabled: number
  wlansGuest: number
}

export function useNetworkHomeJobsRouteParams() {
  return useParams<NetworkHomeJobsParams>()
}

export function useNetworkWLANIdRouteParams() {
  return useParams<NetworkWLANIdParams>()
}

export interface useNetworkMetricsParams {
  siteId: string
  metric: string
  duration: number
}

export function useNetworkWLANsCounts({ siteId }: useCurrentSiteIdParams) {
  const dispatch = useAppDispatch()
  const siteWlans = useAppSelector(siteWlansFetchDataSelector.data)

  useEffect(() => {
    siteId && dispatch(fetchSiteWlansThunk(siteId))
  }, [dispatch, siteId])

  let wlansCount = 0
  let wlansEnabledCount = 0
  let guestCount = 0

  if (siteWlans != null) {
    wlansCount = siteWlans.length
    siteWlans.forEach((siteWlan) => {
      if (siteWlan.enabled) {
        wlansEnabledCount++
      }
      if (siteWlan.portal != null && siteWlan.portal.enabled) {
        guestCount++
      }
    })
  }

  return {
    wlansTotal: wlansCount,
    wlansEnabled: wlansEnabledCount,
    wlansGuest: guestCount,
  }
}

export function useSiteClientsCounts({ siteId }: useCurrentSiteIdParams) {
  const dispatch = useAppDispatch()
  const siteClients = useAppSelector(siteClientsFetchDataSelector.data)

  useEffect(() => {
    siteId && dispatch(fetchSiteClientsThunk(siteId))
  }, [dispatch, siteId])

  let wirelessCount = 0

  if (siteClients != null) {
    wirelessCount = siteClients.length
  }

  return {
    wirelessTotal: wirelessCount,
  }
}

export function useNetworkOrgId() {
  const organizationId = useAppSelector(mistOrganizationIdSelector)
  return organizationId
}

export function useCurrentHomeSiteId({ homeId }: useCurrentHomeSiteIdParams) {
  const dispatch = useAppDispatch()
  const siteId: string = useAppSelector(mistSiteIdSelector)
  const organizationId = useAppSelector(mistOrganizationIdSelector)
  const forceCreate: boolean = false

  useEffect(() => {
    const getNetworkHomeThunk = async () =>
      await dispatch(
        createOrGetNetworkHomeThunk(organizationId, { homeId, forceCreate })
      )
    //if (!siteId && organizationId && homeId) {
    getNetworkHomeThunk()
    //}
  }, [siteId, dispatch, homeId, organizationId, forceCreate])

  return siteId
}

export function useCurrentHomeSiteIdCreate({
  homeId,
}: useCurrentHomeSiteIdParams) {
  const dispatch = useAppDispatch()
  const siteId: string = useAppSelector(mistSiteIdSelector)
  const organizationId = useAppSelector(mistOrganizationIdSelector)
  const forceCreate: boolean = true

  useEffect(() => {
    const getNetworkHomeThunk = async () =>
      await dispatch(
        createOrGetNetworkHomeThunk(organizationId, { homeId, forceCreate })
      )
    if (!siteId && organizationId && homeId) {
      getNetworkHomeThunk()
    }
  }, [siteId, dispatch, homeId, organizationId, forceCreate])

  return siteId
}

export function useSiteRFTemplateID({ siteId }: useCurrentSiteIdParams) {
  const dispatch = useAppDispatch()
  const orgSiteWlans = useAppSelector(orgSiteWlansFetchDataSelector.data)

  useEffect(() => {
    const getOrgSiteWlansThunk = async () =>
      await dispatch(fetchOrgSiteWlansThunk(siteId))
    getOrgSiteWlansThunk()
  }, [siteId, dispatch])
  if (orgSiteWlans !== null) {
    return orgSiteWlans[0].rftemplate_id
  } else {
    return ''
  }
}
